import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import Check from "@material-ui/icons/Check";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";
import CustomConfirm from "ra-custom-confirm";

const useStyles = makeStyles((theme) => ({
  main: {
    padding: 24,
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 24,
  },
  searchPaper: {
    padding: 20,
    marginBottom: 16,
    borderRadius: 8,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  card: {
    marginBottom: 16,
    borderRadius: 8,
  },
  statBox: {
    textAlign: "center",
    padding: 12,
    borderRadius: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 700,
  },
  statLabel: {
    fontSize: 11,
    color: "#666",
    marginTop: 2,
  },
  userCard: {
    flex: 1,
  },
  arrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 16px",
  },
}));

const TransferReferrals = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [sourceEmail, setSourceEmail] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handlePreview = async () => {
    if (!sourceEmail.trim() || !targetEmail.trim()) {
      notify("Both emails are required", "error");
      return;
    }

    setLoading(true);
    setPreview(null);

    try {
      const config = getConfigGet({});
      const res: any = await axios.get(
        `${API}/admin/transfer-referrals?source_email=${encodeURIComponent(sourceEmail.trim())}&target_email=${encodeURIComponent(targetEmail.trim())}`,
        config
      );

      if (res?.data?.status) {
        setPreview(res.data.data);
      } else {
        notify(res?.data?.message || "Preview failed", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to preview", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTransferClick = () => {
    if (!preview) return;
    setConfirmOpen(true);
  };

  const handleConfirmTransfer = async () => {
    setConfirmOpen(false);
    setTransferring(true);

    try {
      const headers = getConfigPost();
      const res: any = await axios.post(
        `${API}/admin/transfer-referrals`,
        { source_email: sourceEmail.trim(), target_email: targetEmail.trim() },
        { headers }
      );

      if (res?.data?.status) {
        const d = res.data.data;
        notify(
          `Transfer complete! Referrals: ${d.referrals_moved}, Commissions: ${d.commissions_moved}, Prize entries: ${d.prize_entries_moved}, Prize results: ${d.prize_results_moved}, Boosts: ${d.boosts_moved}, Balance: £${d.referral_balance_moved}`,
          "info", "", undefined, 10000
        );
        setPreview(null);
        // Re-fetch preview to show updated state
        handlePreview();
      } else {
        notify(res?.data?.message || "Transfer failed", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Transfer failed", "error");
    } finally {
      setTransferring(false);
    }
  };

  const tt = preview?.to_transfer;

  return (
    <div className={classes.main}>
      <Typography className={classes.title}>Transfer Referrals</Typography>
      <Typography className={classes.subtitle}>
        Move all referrals, commissions, prize draw entries, and rewards from one user to another
      </Typography>

      <Paper className={classes.searchPaper} elevation={1}>
        <div className={classes.searchRow}>
          <TextField
            label="Source Email (FROM)"
            variant="outlined"
            size="small"
            value={sourceEmail}
            onChange={(e) => setSourceEmail(e.target.value)}
            style={{ flex: 1 }}
          />
          <SwapHorizIcon color="action" />
          <TextField
            label="Target Email (TO)"
            variant="outlined"
            size="small"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handlePreview}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            Preview
          </Button>
        </div>
      </Paper>

      {preview && (
        <>
          {/* Users side by side */}
          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <Card className={classes.userCard} elevation={1}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Chip label="FROM" size="small" style={{ backgroundColor: "#ffebee", color: "#c62828" }} />
                    <Typography variant="subtitle1">
                      {preview.source.first_name} {preview.source.last_name}
                    </Typography>
                  </Box>
                }
                subheader={preview.source.email}
              />
            </Card>
            <div className={classes.arrow}>
              <SwapHorizIcon style={{ fontSize: 40, color: "#1976d2" }} />
            </div>
            <Card className={classes.userCard} elevation={1}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Chip label="TO" size="small" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }} />
                    <Typography variant="subtitle1">
                      {preview.target.first_name} {preview.target.last_name}
                    </Typography>
                  </Box>
                }
                subheader={preview.target.email}
              />
            </Card>
          </div>

          {/* What will be transferred */}
          <Card className={classes.card} elevation={1}>
            <CardHeader title="Data to Transfer" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#e3f2fd" }}>
                    <Typography className={classes.statValue} style={{ color: "#1565c0" }}>{tt.referrals}</Typography>
                    <Typography className={classes.statLabel}>Referrals</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                    <Typography className={classes.statValue} style={{ color: "#e65100" }}>{tt.commissions.count}</Typography>
                    <Typography className={classes.statLabel}>Commissions ({"\u00A3"}{tt.commissions.total})</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#f3e5f5" }}>
                    <Typography className={classes.statValue} style={{ color: "#7b1fa2" }}>{tt.prize_entries}</Typography>
                    <Typography className={classes.statLabel}>Prize Entries</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#e8f5e9" }}>
                    <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>{tt.prize_results.count}</Typography>
                    <Typography className={classes.statLabel}>Prize Wins ({"\u00A3"}{tt.prize_results.total})</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                    <Typography className={classes.statValue} style={{ color: "#c62828" }}>{tt.boosts}</Typography>
                    <Typography className={classes.statLabel}>Boosts</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box className={classes.statBox} style={{ backgroundColor: "#e0f7fa" }}>
                    <Typography className={classes.statValue} style={{ color: "#00695c" }}>{"\u00A3"}{tt.referral_balance}</Typography>
                    <Typography className={classes.statLabel}>Referral Balance</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider style={{ margin: "20px 0" }} />

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleTransferClick}
                disabled={transferring}
                startIcon={transferring ? <CircularProgress size={20} /> : <SwapHorizIcon />}
              >
                Transfer All
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      <CustomConfirm
        isOpen={confirmOpen}
        title="Confirm Transfer"
        user={`Transfer ALL referral data from ${sourceEmail} to ${targetEmail}?\n\nThis includes: ${tt?.referrals || 0} referrals, ${tt?.commissions?.count || 0} commissions (£${tt?.commissions?.total || 0}), ${tt?.prize_entries || 0} prize entries, ${tt?.prize_results?.count || 0} prize wins (£${tt?.prize_results?.total || 0}), ${tt?.boosts || 0} boosts, £${tt?.referral_balance || 0} referral balance.\n\nThis action cannot be undone.`}
        content={ConfirmContent}
        confirm="Transfer"
        confirmColor="primary"
        ConfirmIcon={Check}
        cancel="Cancel"
        CancelIcon={ErrorOutline}
        onConfirm={handleConfirmTransfer}
        onClose={() => setConfirmOpen(false)}
      />

      <Notification />
    </div>
  );
};

const ConfirmContent = (props: any) => (
  <Typography style={{ whiteSpace: "pre-line" }}>
    {typeof props.user === "string" ? props.user : JSON.stringify(props.user)}
  </Typography>
);

export default TransferReferrals;
